import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage, readJson, SERVER_UNREACHABLE } from '../../lib/api.ts';
import { rememberBuild } from '../../lib/activeBuild.ts';
import { confirmStagingSpend, stagingAckHeaders } from '../../lib/stagingGuard.ts';
import NewDeckModal, { type BuildAnswers } from './NewDeckModal.tsx';
import type { EngineChoiceBlock, PiProfile, SettingsPayload } from '../../../shared/api.ts';

/**
 * Agent flow, intake half: the structured `<NewDeckModal>` collects the brief,
 * images and a few options, this POSTs build-headless, then hands over to
 * `/build/:jobId` (`routes/BuildWatch.jsx`) to watch it.
 *
 * IT DOES NOT WATCH THE BUILD. It used to, and the two halves shared one
 * `phase`, so this component re-attached to whatever build it remembered on
 * every mount — meaning that while a build ran you could never get back to the
 * form to start another. Builds were always backgroundable server-side; this
 * split is what makes that reachable. Starting a build and coming back to
 * `/new` now gives you the form, every time.
 *
 * The only failure it owns is a build that never STARTED (no slot, engine gone
 * mid-intake, name taken). That stays here, on the filled-in form, so the
 * answers survive and the user can just submit again.
 */
export default function NewDeckAgent({ onStartBlank }: { onStartBlank?: () => void }) {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  /** Overrides the modal's own elapsed-time note when the client knows
   *  something the clock cannot infer — today, the one-shot reupload. */
  const [startNote, setStartNote] = useState<string | null>(null);
  // Whether a real stock-photo provider is configured server-side — keyless
  // Picsum doesn't count (random placeholders), so without a key the stock
  // question is never asked.
  const [stockOk, setStockOk] = useState(false);
  // Stock VIDEO is Pexels-only — gates the optional "add a video?" step that
  // follows a stock-photo choice. Off without a Pexels key, so the step is skipped.
  const [videoOk, setVideoOk] = useState(false);
  // Which engine this build starts on, and whether the alternatives can run —
  // `build` on GET /api/settings ({engine, preference, engines:{claude, fast,
  // pi}}). The ⚙ drawer owns the DEFAULT; this modal can override it for one
  // build. Read from the same response as the media flags below, and from the
  // same block the drawer reads, so the two rows can never disagree.
  const [buildEngines, setBuildEngines] = useState<EngineChoiceBlock | null>(null);
  // The saved Pi provider setups, which ride in on the Pi engine block of that
  // same response — so the Provider row costs no second fetch, and can never
  // show a setup the engine row above it disagrees with.
  const [piProfiles, setPiProfiles] = useState<PiProfile[]>([]);
  // {running, maxConcurrent} — how many builds are already in flight. Only a
  // footer hint: a full builder no longer blocks the form.
  const [slots, setSlots] = useState<{ running: number; maxConcurrent: number } | null>(null);
  // `name:size` → the id the ingest preview gave that document. The bytes are
  // already on the server, so the build references them rather than uploading a
  // 23MB deck for the second time. The FILES are still kept alongside: a handle
  // can expire, and then the only way through is to send the document again.
  const uploadsRef = useRef<Record<string, string>>({});
  const mountedRef = useRef(true);
  // Set true on MOUNT, not just false on unmount. StrictMode runs effects
  // mount → cleanup → mount, so a cleanup-only version leaves the ref false for
  // the rest of the component's life — and every guarded response below gets
  // thrown away. That is what greyed out the Photography toggle: `stockOk`
  // never landed, so the modal believed no photo provider was configured.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    api
      .get<SettingsPayload>('/api/settings')
      .then((d) => {
        if (!mountedRef.current) return;
        // Openverse is a keyless CC provider that always works (media.ts sources
        // it when no Unsplash/Pexels key is set), so stock photography is ALWAYS
        // available. Without this a keyless install defaulted every scratch deck
        // to imagery:'none' — the agent then shipped zero images on slides that
        // read as half-empty. Videos still need a Pexels key.
        setStockOk(Boolean(d?.media?.unsplash || d?.media?.pexels || d?.media?.openverse));
        setVideoOk(Boolean(d?.media?.pexels));
        setBuildEngines(d?.build || null);
        setPiProfiles(d?.build?.engines?.pi?.profiles || []);
      })
      .catch(() => {});
    api
      .get<{ running?: number; maxConcurrent?: number }>('/api/wizard/build-status')
      .then((st) => {
        if (mountedRef.current) setSlots({ running: st?.running || 0, maxConcurrent: st?.maxConcurrent || 0 });
      })
      .catch(() => {});
  }, []);

  /** The modal handed us a complete `answers` object plus its file arrays and
   *  the ingest preview's upload handles. */
  function handleModalBuild(a: BuildAnswers, docs: File[], imgs: File[], uploads: Record<string, string>) {
    uploadsRef.current = uploads || {};
    // A fresh press starts from the modal's own elapsed-time note; only the
    // reupload below overrides it. Cleared here rather than in `startBuild`,
    // which the reupload re-enters.
    setStartNote(null);
    void startBuild(a, docs, imgs);
  }

  async function startBuild(
    a: BuildAnswers,
    docs: File[],
    imgs: File[],
    /** `reupload` is the retry after the server said an upload handle had
     *  expired: send every document as a file and claim nothing. Once only. */
    opts: { reupload?: boolean } = {},
  ) {
    // Not on the reupload retry: that is the same build the author already
    // answered for, resent because a handle expired.
    if (!opts.reupload && !(await confirmStagingSpend('building this deck'))) return;
    setStarting(true);
    setStartError(null);
    // A rebuild's pictures never travel through the browser — the wizard pulled
    // them out of the uploaded deck server-side and showed thumbnails from the
    // upload store. So "are there any images" cannot be read off `imgs` alone:
    // doing that sent `imagery: 'none'` for a deck whose every photograph was
    // sitting on the server waiting to be placed.
    const hasPictures = imgs.length > 0 || (a.ownMediaCount || 0) > 0;
    const form = new FormData();
    form.append(
      'answers',
      JSON.stringify({
        title: a.title,
        audience: a.audience,
        format: a.format,
        slideBudget: a.slideBudget,
        ...(a.slideCount ? { slideCount: a.slideCount } : {}),
        colourTreatment: a.colourTreatment,
        ...(a.colourTreatment === 'single' ? { baseColour: a.baseColour } : {}),
        ...(a.colourMatchSource ? { colourMatchSource: true } : {}),
        // Only sent when the author turned section dividers OFF.
        ...(a.sectionIntros === false ? { sectionIntros: false } : {}),
        ...(a.engine === 'codex' && a.buildQuality ? { buildQuality: a.buildQuality } : {}),
        ...(docs.length && a.verbatim ? { verbatim: a.verbatim } : {}),
        ...(docs.length && a.overflow ? { overflow: a.overflow } : {}),
        // THE STOCK ANSWER, passed through. The uploads are not folded in here
        // any more (Jake, 2026-08-10): the server sees the files and decides
        // 'uploaded' vs 'none' from what is actually placeable, so asking for
        // stock while uploading pictures now means both, which is what the
        // Photography switch always looked like it said.
        imagery: a.imagery === 'stock' ? 'stock' : 'none',
        // What the author said each picture shows (the modal's describe step),
        // including anything they marked reference only. Gated on there being
        // pictures: with none, the notes describe nothing.
        ...(hasPictures && a.imageNotes?.length ? { imageNotes: a.imageNotes } : {}),
        // Pictures from the uploaded deck the author binned. Always forwarded:
        // it only ever removes, so there is no state in which honouring it is
        // wrong, and dropping it would silently reinstate what they discarded.
        ...(a.excludeMedia?.length ? { excludeMedia: a.excludeMedia } : {}),
        // Opt-in stock video — only meaningful on the stock path (server-sourced).
        ...(a.video && a.imagery === 'stock' ? { video: true } : {}),
      }),
    );
    if (a.buildModel) form.append('model', a.buildModel);
    // Only the non-default engines name themselves; an older client sends
    // nothing and the server reads that as Claude.
    if (a.engine && a.engine !== 'claude') form.append('engine', a.engine);
    // Pi only: the label of a saved provider setup to run THIS build on. Absent
    // is the everyday case and means the ⚙ Pi fields stand, so it is only ever
    // sent when the modal resolved a real one — an unknown label is a 422.
    if (a.engine === 'pi' && a.piProfile) form.append('piProfile', a.piProfile);
    // Documents the ingest preview already left on the server travel as ids.
    // `opts.reupload` is the retry after the server said a handle had expired:
    // it sends every document as a file and claims nothing.
    const uploads: string[] = [];
    for (const f of docs) {
      const id = opts.reupload ? null : uploadsRef.current[`${f.name}:${f.size}`];
      if (id) uploads.push(id);
      else form.append('file', f);
    }
    if (uploads.length) form.append('uploads', JSON.stringify(uploads));
    for (const f of imgs) form.append('file', f);
    try {
      // Raw fetch rather than `api.postForm`, deliberately: the busy branch
      // below reads `busy`/`reason` off a NON-OK body, which `request()`
      // discards. `readJson` is the part of it that still applies — parse
      // AFTER the ok check and never let the parse itself become the error.
      let res: Response;
      try {
        res = await fetch('/api/wizard/build-headless', {
          method: 'POST',
          credentials: 'same-origin',
          headers: await stagingAckHeaders(),
          body: form,
        });
      } catch {
        // The request never left the browser: the server is gone with no proxy
        // in between. A raw "Failed to fetch" tells the author nothing.
        throw new Error(SERVER_UNREACHABLE);
      }
      const data = await readJson<{
        jobId?: string;
        slug?: string;
        /** An upload handle expired — resend the documents themselves. */
        reupload?: boolean;
        busy?: boolean;
        reason?: string;
        error?: string;
      }>(res);
      if (!res.ok) {
        // A document the preview left on the server has since expired (TTL, or
        // the studio restarted between filling the form in and pressing Build).
        // Nothing is wrong and nothing is lost: send the files themselves. Once
        // only — `reupload` claims no handles, so it cannot come back.
        if (data?.reupload && !opts.reupload) {
          // SAY so. The whole upload starts again from zero, and the modal's
          // only other feedback is a clock still counting from the first Build
          // press — a large deck silently restarting under a 1:20 clock reads as
          // a hang, and people reload, which abandons the request mid-upload.
          // Setting the note also restarts that clock, so the time shown is the
          // time this attempt has taken.
          setStartNote('Sending your brief again…');
          return startBuild(a, docs, imgs, { reupload: true });
        }
        // Can't start the build (every slot busy, or the engine unavailable).
        // Surface it on the filled-in form with a retry.
        if (data?.busy) {
          throw new Error(
            data?.reason || data?.error || 'Every build slot is in use. Try again once one of them finishes.',
          );
        }
        if (data?.error) throw new Error(data.error);
        // Nothing readable came back. A 5xx with no body is the studio not
        // answering (the dev proxy's ECONNREFUSED page lands here); anything
        // else at least gets named, rather than reported as a bare number.
        throw new Error(res.status >= 500 ? SERVER_UNREACHABLE : `The build couldn’t start (${res.status}).`);
      }
      // A 200 whose body won't parse is the same failure wearing a better
      // status — without this, `data.slug` below would throw on null instead.
      if (!data?.jobId) throw new Error(SERVER_UNREACHABLE);
      // So a refresh — or the header's builds indicator — can find it again.
      rememberBuild('new-deck', { jobId: data.jobId, slug: data.slug ?? null, title: a.title });
      navigate(`/build/${data.jobId}`, { state: { slug: data.slug ?? null, title: a.title, fresh: true } });
    } catch (err) {
      if (!mountedRef.current) return;
      setStarting(false);
      // `errorMessage`, not `err.message`: a thrown non-Error stringifies to
      // "[object Object]" and an `Error('')` leaves the sentence hanging after
      // the colon. Every other caller in the client uses it.
      setStartError(`Couldn’t start the build: ${errorMessage(err)}`);
    }
  }

  return (
    <div className="editor editor--new">
      <div className="editor__stage">
        <main className="preview-frame preview-frame--empty">
          <div className="editor__blank">
            <span className="editor__blank-mark" aria-hidden="true" />
            <p className="muted">Setting up your deck…</p>
          </div>
        </main>
      </div>
      <NewDeckModal
        onBuild={handleModalBuild}
        onBlank={onStartBlank}
        onCancel={() => navigate('/')}
        stockOk={stockOk}
        videoOk={videoOk}
        buildEngines={buildEngines}
        piProfiles={piProfiles}
        building={starting}
        buildNote={startNote}
        startError={startError}
        slots={slots}
      />
    </div>
  );
}
